// ============================================================
// COPIES OF ITSELF, DRAWN (plan body-map T2; engine/plate/auras). Fear (X):
// its outline again and again, nested, growing outward and fainter each -
// subtle even at 4 (user). Warp Conduit: copies of it pour out of its chest
// and grow past it, fading - the warp flowing through it. Each grows and
// fades on one clock (a scene the plate writes), so its loop's end is the
// next loop's start and nothing jumps.
// ============================================================

import { OutlineFilter } from './Outline';
import { conduitAt, fearAt, fearRims } from '../../../../engine/plate/auras';
import type { Box, Point } from '../../../../engine/plate/frame';
import tx from '../../../../styles/PlateTraits.module.css';
import type { WrittenNode } from '../scene';

const STILL_T = 1.2;

/** Fear's rim: the silhouette's outline alone, a thin ring round it in the fear colour. */
export function FearDefs({ u }: { u: string }) {
  return <OutlineFilter id={`${u}-fr`} radius={5} flood={tx.fearFlood} />;
}

/** Behind the figure: Fear's nested outlines, each a step larger than the one inside it. */
export function FearBack({ u, x, box }: { u: string; x: number; box: Box }) {
  const still = fearAt(x, box, STILL_T);
  const [cx, cy] = still.centre;
  return (
    <g pointerEvents="none" data-trait="fear" data-fear={x} transform={`translate(${cx} ${cy})`}>
      <g data-g="fear-grow" transform={`scale(${still.scale})`}>
        {Array.from({ length: fearRims(x) }, (_, k) => (
          <g
            key={k}
            data-g={`fear-c${k}`}
            opacity={still.rims[k]}
            transform={`scale(${still.step ** k}) translate(${-cx} ${-cy})`}
          >
            {/* Filled (the plate leaves paths unfilled): the rim is grown from the silhouette's alpha. */}
            <use href={`#${u}-sil`} className={tx.silFill} filter={`url(#${u}-fr)`} />
          </g>
        ))}
      </g>
    </g>
  );
}

/** What the clock writes of Fear at `t`: the growth and each outline's opacity, on one beat. */
export function fearNodes(x: number, box: Box, t: number): WrittenNode[] {
  const at = fearAt(x, box, t);
  return [
    { key: 'fear-grow', attrs: { transform: `scale(${at.scale.toFixed(4)})` } },
    ...at.rims.map((opacity, k) => ({ key: `fear-c${k}`, attrs: { opacity: Number(opacity.toFixed(3)) } })),
  ];
}

export const fearStill = (x: number, box: Box) => fearNodes(x, box, STILL_T);

/** Before the figure: Warp Conduit's copies pouring out of its chest. */
export function ConduitFront({ u, chest: [px, py] }: { u: string; chest: Point }) {
  return (
    <g pointerEvents="none" data-trait="conduit" transform={`translate(${px} ${py})`}>
      {conduitAt(STILL_T).map(({ scale, opacity }, k) => (
        <g key={k} data-g={`pour${k}`} transform={`scale(${scale})`} opacity={opacity}>
          <g className={tx.conduitLines} transform={`translate(${-px} ${-py})`}>
            <use href={`#${u}-art`} />
          </g>
        </g>
      ))}
    </g>
  );
}

/** What the clock writes of the conduit at `t`: each copy's size and fade. */
export const conduitNodes = (t: number): WrittenNode[] =>
  conduitAt(t).map(({ scale, opacity }, k) => ({
    key: `pour${k}`,
    attrs: { transform: `scale(${scale.toFixed(4)})`, opacity: Number(opacity.toFixed(3)) },
  }));

export const conduitStill = () => conduitNodes(STILL_T);
